import React from 'react';
import { Box, Text, ProgressBar, IconButton } from '@primer/react';
import { X, CheckCircle, CloudArrowUp } from 'phosphor-react';
import { ProgressNotification, useProgressStore } from '../stores/useProgressStore';

interface ProgressNotificationItemProps {
  notification: ProgressNotification;
  onDismiss: (id: string) => void;
}

const ProgressNotificationItem: React.FC<ProgressNotificationItemProps> = ({
  notification,
  onDismiss,
}) => {
  const isCompleted = notification.progress >= 100;

  return (
    <Box
      sx={{
        backgroundColor: 'canvas.subtle',
        border: '1px solid',
        borderColor: 'border.default',
        borderRadius: 2,
        padding: 3,
        marginBottom: 2,
        opacity: notification.dismissed ? 0.6 : 1,
        transition: 'opacity 0.2s ease',
      }}
      className="relative"
    >
      <Box className="flex items-start justify-between mb-2">
        <Box className="flex items-center gap-2 flex-1">
          <Box className="flex-shrink-0">
            {isCompleted ? (
              <CheckCircle size={16} className="text-green-500" />
            ) : (
              <CloudArrowUp size={16} className="text-blue-500" />
            )}
          </Box>
          <Box className="flex-1 min-w-0">
            <Text
              sx={{
                fontSize: 1,
                fontWeight: 'semibold',
                color: 'fg.default',
              }}
              className="truncate"
            >
              {notification.title}
            </Text>
          </Box>
        </Box>

        <IconButton
          aria-label="Dismiss notification"
          icon={X}
          variant="invisible"
          size="small"
          onClick={() => onDismiss(notification.id)}
          sx={{
            color: 'fg.muted',
            '&:hover': {
              color: 'fg.default',
            },
          }}
        />
      </Box>

      <Box className="mb-2">
        <Text
          sx={{
            fontSize: 0,
            color: 'fg.muted',
          }}
          className="line-clamp-2"
        >
          {notification.message}
        </Text>
      </Box>

      <Box className="space-y-1">
        <Box className="flex items-center justify-between">
          <Text
            sx={{
              fontSize: 0,
              color: 'fg.muted',
            }}
          >
            Progress: {Math.round(notification.progress)}%
          </Text>
          {isCompleted && (
            <Text
              sx={{
                fontSize: 0,
                color: 'success.fg',
                fontWeight: 'semibold',
              }}
            >
              Completed
            </Text>
          )}
        </Box>

        <ProgressBar
          progress={notification.progress}
          barSize="small"
          sx={{
            width: '100%',
            '& [data-progress-bar]': {
              backgroundColor: isCompleted ? 'success.emphasis' : 'accent.emphasis',
            },
          }}
          animated={!isCompleted}
        />
      </Box>
    </Box>
  );
};

interface ProgressNotificationsProps {
  className?: string;
  maxItems?: number;
}

export const ModelProgressNotifications: React.FC<ProgressNotificationsProps> = ({
  className = '',
  maxItems = 5,
}) => {
  const { notifications, dismiss } = useProgressStore();

  // Filter out dismissed notifications older than 30 seconds
  const visibleNotifications = notifications
    .filter((notification) => {
      if (!notification.dismissed) return true;
      // Keep dismissed notifications for a short time to allow for fade out animation
      return true;
    })
    .slice(0, maxItems);

  if (visibleNotifications.length === 0) {
    return null;
  }
};

// Optional: Hook for easier integration
export const useProgressNotifications = () => {
  const { notifications, addOrUpdate, dismiss, remove } = useProgressStore();

  return {
    notifications,
    addOrUpdate,
    dismiss,
    remove,
    hasActiveUploads: notifications.some((n) => n.progress < 100 && !n.dismissed),
    completedCount: notifications.filter((n) => n.progress >= 100).length,
    activeCount: notifications.filter((n) => n.progress < 100 && !n.dismissed).length,
  };
};

export default ModelProgressNotifications;
